import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import OrderController from '@/services/OrderController';
import ProfileController from '@/services/ProfileController';
import { checkForeignPhone, checkNumber, checkPhoneSoft } from '@/utils/validation';
import { transformPhoneSoft } from '@/utils/funcs';
import { DELAY_OPTIONS, PAYMENT_TYPES } from '@/utils/constants';

const DEFAULT_REQUIRED_ORDER_FIELDS = {
	price: {
		mode: 'optional',
	},
	area: {
		mode: 'optional',
	},
	realClient: {
		mode: 'required',
	},
	freeClient: {
		children: {
			name: {
				mode: null,
			},
			phone: {
				mode: null,
			},
			address: {
				mode: null,
			},
			location: {
				mode: null,
			},
		},
	},
};

// A free-client order can carry two different addresses, independently switched
// on per shop: `clientAddress` is the free text the operator types (what the
// driver reads), `clientLocation` is the geocoded one picked through
// AddressWrapper — `street` plus the coordinates of the suggestion chosen or
// the pin dropped. Only the latter can put the order on the map or price it by
// distance; only the former can hold whatever the customer actually said.
const EMPTY_CLIENT_LOCATION = { street: '', location: null };

const getPickedLatLng = clientLocation => {
	const { lat, lng } = clientLocation?.location || {};
	return typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : null;
};

// Where a saved client address points: the pin a rider dropped at the door
// beats the geocoder's. Same order as the server's getAddressLocation.
const getAddressLatLng = address => {
	const lat = address?.droppedLocationPin?.latitude ?? address?.l?.lat;
	const lng = address?.droppedLocationPin?.longitude ?? address?.l?.lng;
	return typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : null;
};

export const useNewOrderForm = ({
	user,
	isMasterOrAdmin,
	shop,
	client,
	setOperating,
	setNotOperatingReason,
	setWarningMessage,
	setNewOrderMobileVisible,
}) => {
	const [comments, setComments] = useState('');
	const [price, setPrice] = useState('');
	const [clientName, setClientName] = useState('');
	const [clientPhone, setClientPhone] = useState('');
	const [clientAddress, setClientAddress] = useState('');
	const [clientLocation, setClientLocation] = useState(EMPTY_CLIENT_LOCATION);

	const [areaPriceProm, setAreaPriceProm] = useState('');
	// what the shop is charged for this order; separate from the rider's fee
	const [areaShopFee, setAreaShopFee] = useState('');

	const [errorPrice, setErrorPrice] = useState(false);
	const [errorPhone, setErrorPhone] = useState(false);
	const [errorName, setErrorName] = useState(false);
	const [errorAddress, setErrorAddress] = useState(false);
	const [errorLocation, setErrorLocation] = useState(false);

	const [selectedAreaIdx, setSelectedAreaIdx] = useState(null);
	// Which destination the area was picked for («Αυτόματα» only, see below).
	const [areaPickKey, setAreaPickKey] = useState(null);
	const [paymentType, setPaymentType] = useState(PAYMENT_TYPES.CASH);
	const [asSimpleOrder, setAsSimpleOrder] = useState(false);
	const [isSendingOrder, setIsSendingOrder] = useState(false);

	// The shop's own minimum wins over the city's; null/undefined means it follows
	// the city. An operator reads it off the shop they picked, a shop account off
	// its profile — the same two places requiredOrderFields comes from below.
	// The city fallback is what applies with no shop chosen yet.
	const cityMinOrderDelayMinutes = user?.cityLocation?.minOrderDelayMinutes;
	const shopMinOrderDelayMinutes = isMasterOrAdmin
		? shop.selectedShop?.minOrderDelayMinutes
		: user?.minOrderDelayMinutes;

	// `?? 0` matters: comparing against undefined is false for every option, which
	// would leave the delay dropdown empty and make the order unsendable.
	const minOrderDelayMinutes = shopMinOrderDelayMinutes ?? cityMinOrderDelayMinutes ?? 0;

	// What resetFields goes back to: it clears the selected shop, so for an
	// operator the shop's minimum no longer applies, while a shop account keeps
	// its own.
	const baseMinOrderDelayMinutes = isMasterOrAdmin
		? (cityMinOrderDelayMinutes ?? 0)
		: minOrderDelayMinutes;

	const delayOptionsFiltered = useMemo(() => {
		return DELAY_OPTIONS.filter(it => it.value >= minOrderDelayMinutes);
	}, [minOrderDelayMinutes]);

	const [delay, setDelay] = useState(minOrderDelayMinutes || delayOptionsFiltered?.[0]?.value);
	const delayRef = useRef();

	// Picking a shop with a longer minimum has to push the chosen delay up; a
	// delay already longer than the minimum is the operator's own pick and stays.
	useEffect(() => {
		setDelay(d => (d > minOrderDelayMinutes ? d : minOrderDelayMinutes));
	}, [minOrderDelayMinutes]);

	const areas = user?.cityLocation?.areas;

	// «Αυτόματα από το στίγμα» (cities.areaMode): the area comes from where the
	// order is going. A shop no longer picks it; an admin gets it preselected and
	// may change it (the server remembers the change for that spot).
	const isAutoArea =
		user?.cityLocation?.areaMode === 'auto' && user?.cityLocation?.areaBoundaries?.length > 0;

	const destination = asSimpleOrder
		? getPickedLatLng(clientLocation)
		: getAddressLatLng(client.selectedClient?.a?.[client.selectedAddressIndex]);

	// The server's answer for the last point asked about. Only ever written when
	// the answer arrives, so a stale one is told apart by its key.
	const [pinLookup, setPinLookup] = useState({ key: null, area: undefined });
	const destinationKey = destination ? `${destination.lat},${destination.lng}` : null;

	// undefined: not known yet; null: the address has no pin; '': the pin is
	// outside every area; otherwise the area's name.
	const pinArea = !isAutoArea
		? undefined
		: !destinationKey
			? null
			: pinLookup.key === destinationKey
				? pinLookup.area
				: undefined;

	useEffect(() => {
		if (!isAutoArea || !destination) {
			return;
		}
		let alive = true;
		ProfileController.getAreaForLocation(
			destination.lat,
			destination.lng,
			user?.cityLocation?.id,
		).then(res => {
			if (!alive) {
				return;
			}
			const area = res?.error ? undefined : res?.area;
			setPinLookup({ key: destinationKey, area });

			// An admin starts from the pin's area, with its prices, and can change
			// it (the server remembers a change for that spot). A pin in no area
			// clears whatever an earlier address had selected.
			if (isMasterOrAdmin && area !== undefined) {
				const idx = area ? (areas?.indexOf(area) ?? -1) : -1;
				setSelectedAreaIdx(idx === -1 ? null : idx);
				setAreaPickKey(destinationKey);
				setAreaPriceProm(idx === -1 ? '' : (shop.selectedShop?.feeList?.[idx] ?? ''));
				setAreaShopFee(idx === -1 ? '' : (shop.selectedShop?.shopFeeList?.[idx] ?? ''));
			}
		});
		return () => {
			alive = false;
		};
	}, [isAutoArea, destinationKey, isMasterOrAdmin, shop.selectedShop?.id]);

	// In «Αυτόματα» an area picked for one address must not carry over to
	// another: it would bill the new address by the old area and the server
	// would remember that area for it. Such a pick counts as no pick at all.
	const areaPickStale = isAutoArea && selectedAreaIdx !== null && areaPickKey !== destinationKey;
	const effectiveAreaIdx = areaPickStale ? null : selectedAreaIdx;

	const selectAreaIdx = idx => {
		setSelectedAreaIdx(idx);
		setAreaPickKey(destinationKey);
	};

	// A shop cannot send an order the server would refuse for its pin.
	const shopBlockedByPin = isAutoArea && !isMasterOrAdmin && (pinArea === '' || pinArea === null);

	const requiredOrderFields =
		(isMasterOrAdmin ? shop.selectedShop?.requiredOrderFields : user.requiredOrderFields) ??
		DEFAULT_REQUIRED_ORDER_FIELDS;

	const freeClientChildren = requiredOrderFields?.freeClient?.children;

	const freeClientAllowed =
		requiredOrderFields?.realClient?.mode !== 'required' &&
		(freeClientChildren?.name?.mode ||
			freeClientChildren?.phone?.mode ||
			freeClientChildren?.address?.mode ||
			freeClientChildren?.location?.mode);

	useEffect(() => {
		if (!requiredOrderFields?.realClient?.mode && freeClientAllowed) {
			setAsSimpleOrder(true);
		}
	}, [freeClientAllowed, requiredOrderFields?.realClient?.mode]);

	const validatePrice = () => {
		if (paymentType === PAYMENT_TYPES.CARD) {
			setErrorPrice(false);
			return false;
		}
		const tempPrice = price != null ? `${price}`.replaceAll(',', '.') : '0';

		const error =
			requiredOrderFields?.price?.mode === 'required'
				? !(tempPrice > 0)
				: !price
					? false
					: !checkNumber(tempPrice);
		setErrorPrice(error);

		return error;
	};

	useEffect(() => {
		validatePrice();
	}, [paymentType]);

	const validateName = () => {
		const error =
			asSimpleOrder &&
			requiredOrderFields?.freeClient?.children?.name?.mode === 'required' &&
			!clientName;

		setErrorName(error);
		return error;
	};

	const validateAddress = () => {
		const error =
			asSimpleOrder &&
			requiredOrderFields?.freeClient?.children?.address?.mode === 'required' &&
			!clientAddress;
		setErrorAddress(error);
		return error;
	};

	// Mirrors AddressWrapper's own blur check (which owns errorLocation while the
	// user is typing); this is the submit-time pass. A picked location only
	// counts once it has coordinates, so a street typed but never resolved is
	// rejected even when the field is merely optional — that half-filled state
	// is the one that looks answered but locates nothing.
	const validateLocation = () => {
		const mode = requiredOrderFields?.freeClient?.children?.location?.mode;

		if (!asSimpleOrder || !mode) {
			setErrorLocation(false);
			return false;
		}

		const hasStreet = !!clientLocation?.street?.trim();
		const hasCoords = !!getPickedLatLng(clientLocation);

		const error = mode === 'required' ? !(hasStreet && hasCoords) : hasStreet && !hasCoords;

		setErrorLocation(error);
		return error;
	};

	const validatePhone = (isSimple = asSimpleOrder) => {
		const phone = clientPhone;
		// A free-client order stores the phone as plain text, so there is no fp flag
		// to set and no «Εξωτερικού» checkbox to tick — a foreign number is simply
		// accepted on its own terms. Without this a foreign customer cannot be
		// taken as a simple order at all: the send button stays disabled.
		const error =
			phone?.length > 0
				? !checkPhoneSoft(phone) && !checkForeignPhone(phone)
				: isSimple && requiredOrderFields?.freeClient?.children?.phone?.mode === 'required';
		setErrorPhone(error);
		return error;
	};

	const onDelaySelect = value => {
		setDelay(value);
		delayRef.current.parentToggleModal(false);
	};

	const onSetAsSimpleOrder = value => {
		if (value) {
			validatePhone(value);
		} else {
			client.setClientSearchText(clientPhone);
			// Going back to the saved-client search unmounts the location field, so
			// a street left typed-but-unresolved would otherwise keep the send
			// button disabled with nothing on screen to explain it.
			setErrorLocation(false);
		}
		setAsSimpleOrder(value);
	};

	const resetFields = () => {
		setErrorPrice(false);

		client.setClientSearchText('');

		setPrice('');
		setComments('');
		setClientName('');
		setClientPhone('');
		setClientAddress('');
		setClientLocation(EMPTY_CLIENT_LOCATION);

		setDelay(baseMinOrderDelayMinutes || 0);
		client.setClientSearchText('');
		shop.setShopSearchText('');

		setAreaPriceProm('');
		setAreaShopFee('');

		onSetAsSimpleOrder(
			isMasterOrAdmin ? false : !requiredOrderFields?.realClient?.mode && freeClientAllowed,
		);

		client.setFoundClients([]);
		shop.setFoundShops([]);
		client.setSelectedClient(null);
		shop.setSelectedShop(null);
		client.setSelectedAddressIndex(null);

		setErrorName(false);
		setErrorPhone(false);
		setErrorAddress(false);
		setErrorLocation(false);
		setSelectedAreaIdx(null);

		setPaymentType(PAYMENT_TYPES.CASH);

		client.dropdownRef?.current?.parentToggleModal(false);
		shop.dropdownShopRef?.current?.parentToggleModal(false);
	};

	const onRemoveShop = () => {
		shop.setSelectedShop(null);
		shop.setFoundShops([]);

		setPrice('');
		setComments('');
		setClientName('');
		setClientPhone('');
		setClientAddress('');
		setClientLocation(EMPTY_CLIENT_LOCATION);

		shop.setShopSearchText('');

		setAsSimpleOrder(false);

		setErrorName(false);
		setErrorAddress(false);
		setErrorLocation(false);
		setErrorPhone(false);
		setErrorPrice(false);

		setSelectedAreaIdx(null);
		client.setSelectedClient(null);

		client.setSelectedAddressIndex(null);

		client.setFoundClients([]);

		client.setClientSearchText('');
	};

	const saveDisabled = () => {
		if (!client.selectedClient && !asSimpleOrder) {
			return true;
		}
		if (requiredOrderFields?.realClient?.mode === 'required' && !client.selectedClient) {
			return true;
		}
		if (client.selectedClient) {
			if (client.selectedAddressIndex === null || client.selectedAddressIndex < 0) {
				return true;
			}
		}
		if (errorName || errorAddress || errorLocation || errorPhone) {
			return true;
		}

		if (errorPrice) {
			return true;
		}

		if (shopBlockedByPin) {
			return true;
		}

		return false;
	};

	const addButtonsDisabled =
		client.isSearchingClient ||
		!(client.foundClients?.length === 0 && client.clientSearchText?.length >= 7);

	const submitOrder = async () => {
		try {
			if (isSendingOrder) {
				toast.error('Η παραγγελία στέλνεται...');
				return;
			}

			if (isMasterOrAdmin && !shop.selectedShop) {
				toast.error('Παρακαλώ επιλέξτε Κατάστημα');
				setIsSendingOrder(false);
				return;
			}

			const errorPrice = validatePrice();
			const errorClientName = validateName();
			const errorClientAddress = validateAddress();
			const errorClientLocation = validateLocation();
			const errorClientPhone = validatePhone();

			if (errorPrice) {
				toast.error('Η τιμή πρέπει να είναι νούμερο > 0');
				setIsSendingOrder(false);
				return;
			}

			if (errorClientName) {
				toast.error('Το όνομα δεν είναι έγκυρο');
				setIsSendingOrder(false);
				return;
			}

			if (errorClientAddress) {
				toast.error('Η διεύθυνση δεν είναι έγκυρη');
				setIsSendingOrder(false);
				return;
			}

			if (errorClientLocation) {
				toast.error('Επίλεξε τοποθεσία από τα αποτελέσματα ή από τον χάρτη');
				setIsSendingOrder(false);
				return;
			}

			if (errorClientPhone) {
				toast.error('Το τηλέφωνο δεν είναι έγκυρο');
				setIsSendingOrder(false);
				return;
			}

			setIsSendingOrder(true);

			if (shopBlockedByPin) {
				toast.error(
					pinArea === ''
						? 'Το στίγμα είναι εκτός περιοχών εξυπηρέτησης'
						: 'Η διεύθυνση δεν έχει στίγμα στον χάρτη',
				);
				setIsSendingOrder(false);
				return;
			}

			if (
				requiredOrderFields?.area?.mode === 'required' &&
				!(effectiveAreaIdx !== null) &&
				!isAutoArea
			) {
				toast.error('Παρακαλώ επιλέξτε περιοχή');
				setIsSendingOrder(false);
				return;
			}

			if (requiredOrderFields?.realClient?.mode === 'required' && !client.selectedClient?.id) {
				toast.error('Παρακαλώ επιλέξτε πελάτη');
				setIsSendingOrder(false);
				return;
			}

			if (client.selectedClient) {
				if (!(client.selectedAddressIndex > 0 || client.selectedAddressIndex === 0)) {
					toast.error('Παρακαλώ επιλέξτε διεύθυνση');
					setIsSendingOrder(false);
					return;
				}
			}

			const freeClientValues = {
				clientName,
				clientPhone: transformPhoneSoft(clientPhone),
				// The order's readable address. Falls back to the picked street so a
				// shop that only shows the location field still leaves the driver
				// something to read.
				clientAddress: clientAddress?.trim() || clientLocation?.street?.trim() || '',
				// Stored as the order address's `l`, the same field a saved client
				// address carries — that is what the live map, the shop-to-client
				// radius and the distance-based fees read.
				clientLocation: getPickedLatLng(clientLocation),
			};
			const orderData = {
				shopId: shop.selectedShop?.id,
				clientId: client.selectedClient?.id,
				address: client.selectedClient?.a?.[client.selectedAddressIndex],
				price: price != null ? `${price}`.replaceAll(',', '.') : '0',
				paymentType,
				comments,
				delayMinutes: delay,
				...(asSimpleOrder && freeClientValues),
				// In «Αυτόματα» a shop sends no area: the server takes the pin's.
				area: isAutoArea && !isMasterOrAdmin ? undefined : areas?.[effectiveAreaIdx],
				fee:
					user?.cityLocation?.withFees &&
					isMasterOrAdmin &&
					!areaPickStale &&
					requiredOrderFields?.area?.mode &&
					parseFloat(areaPriceProm),
				shopFee:
					user?.cityLocation?.withShopFees &&
					isMasterOrAdmin &&
					!areaPickStale &&
					requiredOrderFields?.area?.mode &&
					parseFloat(areaShopFee),
			};

			const res = await OrderController.add(orderData);
			if (!res?.error) {
				toast.success('Η Παραγγελία στάλθηκε!');
				resetFields();
				client.setClientSearchText('');
				setIsSendingOrder(false);

				setNewOrderMobileVisible(false);
			} else {
				setIsSendingOrder(false);
				if (res?.code === 335) {
					toast.error('Υπηρεσία εκτός ωραρίου');
					return;
				}

				if (res?.code === 389) {
					setOperating(false);
					setNotOperatingReason(res?.notOperatingReason);
					setWarningMessage(res?.warningMessage);
				}
				toast.error(
					`Κάποιο πρόβλημα προέκυψε${typeof res.error === 'string' || res?.code ? ':' : ''}${
						typeof res.error === 'string' ? ` ${res.error}` : ''
					}${res?.code ? ` (${res.code})` : ''}`,
				);
			}
		} catch (err) {
			console.log(err);
			toast.error(`Κάποιο πρόβλημα προέκυψε${err}`);
			setIsSendingOrder(false);
		}
	};

	return {
		comments,
		setComments,
		price,
		setPrice,
		clientName,
		setClientName,
		clientPhone,
		setClientPhone,
		clientAddress,
		setClientAddress,
		clientLocation,
		setClientLocation,
		areaPriceProm,
		setAreaPriceProm,
		areaShopFee,
		setAreaShopFee,
		errorPrice,
		errorPhone,
		errorName,
		errorAddress,
		errorLocation,
		setErrorLocation,
		selectedAreaIdx: effectiveAreaIdx,
		setSelectedAreaIdx: selectAreaIdx,
		paymentType,
		setPaymentType,
		asSimpleOrder,
		isSendingOrder,
		delay,
		delayRef,
		delayOptionsFiltered,
		onDelaySelect,
		areas,
		isAutoArea,
		pinArea,
		requiredOrderFields,
		freeClientAllowed,
		validatePrice,
		validateName,
		validateAddress,
		validateLocation,
		validatePhone,
		onSetAsSimpleOrder,
		resetFields,
		onRemoveShop,
		saveDisabled,
		addButtonsDisabled,
		submitOrder,
	};
};
